/**
 * Flow Visualization Constants
 *
 * Layout constants and color definitions for the Sankey-style flow visualization.
 * Based on the specification in docs/spec/sankey-visualization-engine.md
 *
 * WP-FLOW-6: Updated to use light background + border styling (matching dashboard mini flow)
 */

// ============================================================================
// Canvas Dimensions
// ============================================================================

export const CANVAS = {
  WIDTH: 1200,
  HEIGHT: 700,
  MOBILE_WIDTH: 800,
  MOBILE_HEIGHT: 600,
} as const;

// ============================================================================
// Responsive Breakpoints
// ============================================================================

export const RESPONSIVE = {
  /** Mobile breakpoint (below this uses horizontal scroll) */
  MOBILE_BREAKPOINT: 768,
  /** Tablet breakpoint (between mobile and desktop) */
  TABLET_BREAKPOINT: 1200,
  /** Minimum canvas width to ensure readability on mobile */
  MIN_CANVAS_WIDTH: 800,
} as const;

// ============================================================================
// Column Positions
// ============================================================================

export const COLUMNS = {
  /**
   * Inflow nodes X position - offset right to leave room for left-side labels
   * WP-FLOW-7: With narrower inflow bars (24px), we can position closer to labels
   */
  INFLOW_X: 150,
  /**
   * Account nodes X position - centered in canvas
   * WP-FLOW-7: Adjusted for wider account nodes (160px)
   * Center of canvas (600) minus half account width (80) = 520
   */
  ACCOUNT_X: 520,
  /**
   * Outflow nodes X position - offset left to leave room for right-side labels
   * WP-FLOW-7: With narrower outflow bars (24px), we can position further right
   */
  OUTFLOW_X: 1050,
} as const;

// ============================================================================
// Node Dimensions
// ============================================================================

export const NODE = {
  // Income/Expense nodes - narrow bar style (labels outside)
  // WP-FLOW-7: Reduced from 80px to 24px
  WIDTH: 24,
  // Account nodes - wider to fit 20 characters
  // WP-FLOW-7: Increased from 100px to 160px
  ACCOUNT_WIDTH: 160,
  MIN_HEIGHT: 30,
  GAP: 8,
  ACCOUNT_HEIGHT: 60, // Fixed height for accounts
  LUMPED_HEIGHT: 70,
  BORDER_RADIUS: 4,
  BORDER_WIDTH: 2,
} as const;

// ============================================================================
// Margins
// ============================================================================

export const MARGIN = {
  TOP: 60,
  BOTTOM: 40,
  LEFT: 20,
  RIGHT: 20,
} as const;

// ============================================================================
// Tier Dividers
// ============================================================================

export const TIER = {
  DIVIDER_HEIGHT: 15,
  LABEL_OFFSET: 20,
} as const;

// ============================================================================
// Edge Dimensions
// ============================================================================

export const EDGE = {
  MIN_STROKE_WIDTH: 2,
  MAX_STROKE_WIDTH: 40,
} as const;

// ============================================================================
// Color Definitions (WP-FLOW-6: Light Background + Border Style)
// ============================================================================

export const COLORS = {
  // ============================================================================
  // Node Colors (Light Background + Border Style)
  // ============================================================================

  /** Inflow nodes (income) - green palette */
  inflow: {
    background: '#DCFCE7', // green-100
    border: '#16A34A', // green-600
    text: '#166534', // green-800
  },

  /** Outflow nodes (expenses) - red palette */
  outflow: {
    background: '#FEE2E2', // red-100
    border: '#DC2626', // red-600
    text: '#991B1B', // red-800
  },

  /** Asset accounts by tier */
  account: {
    tierA: {
      background: '#DBEAFE', // blue-100
      border: '#2563EB', // blue-600
      text: '#1E40AF', // blue-800
    },
    tierB: {
      background: '#E9D5FF', // purple-100
      border: '#9333EA', // purple-600
      text: '#6B21A8', // purple-800
    },
    tierC: {
      background: '#F3F4F6', // gray-100
      border: '#6B7280', // gray-500
      text: '#374151', // gray-700
    },
    tierD: {
      background: '#EFEBE9', // brown-50
      border: '#8D6E63', // brown-400
      text: '#5D4037', // brown-700
    },
  },

  /** Liability accounts (credit, loan, other_liability) - amber palette */
  liability: {
    background: '#FEF3C7', // amber-100
    border: '#D97706', // amber-600
    text: '#92400E', // amber-800
  },

  /** Transfer flows - orange palette */
  transfer: {
    background: '#FFEDD5', // orange-100
    border: '#EA580C', // orange-600
    text: '#9A3412', // orange-800
  },

  // ============================================================================
  // Shorthand color references (for backward compatibility and simple usage)
  // These provide single color values for cases where nested structure is not needed
  // ============================================================================

  /** Income node border color (for edges/simple coloring) */
  income: '#16A34A', // green-600

  /** Expense node border color (for edges/simple coloring) */
  expense: '#DC2626', // red-600

  /** Tier A account border color (for edges/simple coloring) */
  accountTierA: '#2563EB', // blue-600

  /** Tier B account border color (for edges/simple coloring) */
  accountTierB: '#9333EA', // purple-600

  /** Tier C account border color (for edges/simple coloring) */
  accountTierC: '#6B7280', // gray-500

  // ============================================================================
  // Edge Colors (with transparency for layered effect)
  // ============================================================================
  edge: {
    income: 'rgba(22, 163, 74, 0.35)', // green-600 with opacity
    expense: 'rgba(220, 38, 38, 0.35)', // red-600 with opacity
    transfer: 'rgba(234, 88, 12, 0.35)', // orange-600 with opacity
  },

  edgeHover: {
    income: 'rgba(22, 163, 74, 0.6)',
    expense: 'rgba(220, 38, 38, 0.6)',
    transfer: 'rgba(234, 88, 12, 0.6)',
  },

  // ============================================================================
  // Text Colors (for non-node contexts)
  // ============================================================================
  text: {
    primary: '#212121',
    secondary: '#757575',
    negative: '#DC2626', // red-600 for negative balances
  },

  // ============================================================================
  // Background Colors
  // ============================================================================
  background: {
    card: '#FFFFFF',
    divider: '#EEEEEE',
  },
} as const;

// ============================================================================
// Tier Configuration
// ============================================================================

export const TIER_CONFIG = {
  A: {
    label: 'Operating',
    colors: COLORS.account.tierA,
    alwaysExpanded: true,
  },
  B: {
    label: 'Savings & Investments',
    colors: COLORS.account.tierB,
    alwaysExpanded: false,
  },
  C: {
    label: 'Other Assets',
    colors: COLORS.account.tierC,
    alwaysExpanded: false,
  },
  D: {
    label: 'Other Liabilities',
    colors: COLORS.account.tierD,
    alwaysExpanded: false,
  },
} as const;

// ============================================================================
// Accessibility
// ============================================================================

export const A11Y = {
  FOCUS_RING: '2px solid #1565C0',
  FOCUS_OFFSET: '2px',
} as const;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the color configuration for an account based on tier
 * (Use isLiabilityAccount from types.ts for liability detection)
 */
export function getAccountTierColors(tier: 'A' | 'B' | 'C' | 'D'): {
  background: string;
  border: string;
  text: string;
} {
  return TIER_CONFIG[tier].colors;
}
